import { forwardRef } from 'react';

import { Flex } from '../flex';
import { type SvgIcon, FindInPageOutlinedIcon } from '../icons';
import { Text, type TextProps } from '../text';
import { type ColorPalette } from '../theme';
import { mergeSx, type SxPropsWithTheme } from '../utils';

export interface NoRowsOverlayProps {
  title?: string;
  description?: React.ReactNode;
  icon?: typeof SvgIcon;
  action?: JSX.Element;
  slotProps?: {
    title?: {
      variant?: TextProps['variant'];
    };
    wrapper?: {
      sx?: SxPropsWithTheme;
      disablePadding?: boolean;
    };
    icon?: {
      fontSize?: number;
      color?: `${ColorPalette}`;
    };
  };
}

const renderDescription = (description: React.ReactNode) => {
  if (!description) return null;

  if (typeof description === 'string') {
    return <Text sx={{ textAlign: 'center' }}>{description}</Text>;
  }

  return description;
};

const NoRowsOverlay = forwardRef<HTMLDivElement, NoRowsOverlayProps>(
  (props: Readonly<NoRowsOverlayProps>, ref) => {
    const { title, description, icon, action, slotProps = {} } = props;
    const { icon: iconProps, wrapper: wrapperProps, title: titleProps } = slotProps ?? {};

    const Icon = icon ?? FindInPageOutlinedIcon;

    return (
      <Flex
        ref={ref}
        sx={mergeSx(
          (theme) => ({
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(4),
            p: wrapperProps?.disablePadding ? theme.spacing(0) : theme.spacing(25, 0),
            width: '100%',
          }),
          wrapperProps?.sx,
        )}
      >
        <Icon
          sx={{
            color: (theme) =>
              iconProps?.color ? theme.palette[iconProps.color].main : theme.palette.grey[400],
            fontSize: iconProps?.fontSize ?? 100,
          }}
        />
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
          <Text variant={titleProps?.variant ?? 'h3'} sx={{ textAlign: 'center' }}>
            {title ?? 'ไม่พบข้อมูลที่ตรงกับการค้นหา'}
          </Text>
          {renderDescription(description)}
        </Flex>
        {action ?? null}
      </Flex>
    );
  },
);

NoRowsOverlay.displayName = 'NoRowsOverlay';

export { NoRowsOverlay };
